import $ from 'jquery';
import _ from 'underscore';

import {
  MISSING,
  SYMBOL_FONT_FACE_TO_UNICODE_CHAR_MAP,
  WYCIWYPPart,
} from 'notebook/mediocre/clipboard/WyciwypConstants';
import {ListType} from 'notebook/notes/lists/ListConstants';

const {BULLET, NUMBER} = ListType;

// Helpers for processing Word lists.
function _hasBulletStyle(style: string): boolean {
  return /mso-level-number-format:\s*bullet\s*;/.test(style);
}

function _isListBullet(el: HTMLElement): boolean {
  // @ts-expect-error
  return /mso-list\s*:\s*Ignore/i.test(el.attributes.style != null ? el.attributes.style.value : undefined);
}

function _removeWhere(el: HTMLElement, filter: (el: HTMLElement) => boolean): void {
  let childEl;
  const toProcess = Array.from($(el).children());
  while (toProcess[toProcess.length - 1] != null) {
    childEl = toProcess.pop()!;
    if (filter(childEl)) {
      $(childEl).remove();
    } else {
      toProcess.push(...Array.from($(childEl).children() || []));
    }
  }
}

function isWord(html: string): boolean {
  return /(class="?Mso|style="[^"]*\bmso-|w:WordDocument)/.exec(html) != null;
}

/**
 * Determine the symbol font of a style attribute, parsing both the standard font-family and the non-standard
 * mso-symbol-font-family CSS properties.
 *
 * @param {CSSStyleDeclaration} style
 * @return {string?} - name of the symbol font if one could be found.
 */
function getSymbolFontFamily(style: string): string | null {
  const msoCharType = parseStyleAttribute(style, 'mso-char-type');
  if (msoCharType === 'symbol') {
    return parseStyleAttribute(style, 'mso-symbol-font-family');
  }
  const fontFamily = parseStyleAttribute(style, 'font-family');
  if (fontFamily! in SYMBOL_FONT_FACE_TO_UNICODE_CHAR_MAP) {
    return fontFamily;
  }
  return null;
}

/**
 * Convert symbol characters in `contents` to their unicode equivalent if the `style` attribute indicates
 * that the text is rendered with a symbol font.
 *
 * @param {string} contents - textContent of an HTMLElement
 * @param {CSSStyleDeclaration} style - the style attribute of an HTMLElement
 * @param {Array} warnings - a mutable array in which missing symbol errors will be aggregated
 * @return {string}
 */
function resolveSymbolsToUnicode(contents: string, style: string, warnings: Array<string>): string {
  const msoSymbolFontFamily = getSymbolFontFamily(style);
  if (!msoSymbolFontFamily) {
    return contents;
  }
  if (!(msoSymbolFontFamily in SYMBOL_FONT_FACE_TO_UNICODE_CHAR_MAP)) {
    warnings.push(`Unexpected mso-symbol-font-family: ${msoSymbolFontFamily}`);
    return contents;
  }
  // @ts-expect-error
  const charMap = SYMBOL_FONT_FACE_TO_UNICODE_CHAR_MAP[msoSymbolFontFamily];
  let symbols = '';
  for (const char of contents) {
    const code = char.charCodeAt(0);
    const symbol = code < charMap.length ? charMap[code] : MISSING;
    if (symbol === MISSING) {
      warnings.push(`Unexpected mso-symbol-font-family: ${msoSymbolFontFamily}, charCode: ${code}`);
    }
    symbols += symbol;
  }
  return symbols;
}

interface ProcessWordOptions {}

interface ExtendedOptions extends ProcessWordOptions {
  extraSanitizeText: (contents: string, textNode: Node) => string;
}

function processWord(
  html: string,
  processNode: (el: HTMLElement, options: ExtendedOptions) => Array<WYCIWYPPart>,
  options: ProcessWordOptions
): Array<WYCIWYPPart> {
  let groups;
  let listLevel;
  let listName;
  let listStyle;
  let listType;
  // Warnings are tracked but are currently unused, because users have not recently reported issues in
  // copy-pasting from Word. If they do, then logging.dump {html, warnings} so we can debug.
  const warnings: Array<string> = [];
  const listTypes: {[key: string]: ListType} = {};
  const listKey = (name: string, level: string): string => `${name}-${level}`;

  function setListStyle(name: string, level: string, type: ListType): void {
    listTypes[listKey(name, level)] = type;
  }

  function getListStyle(name: string, level: string): ListType {
    const key = listKey(name, level);
    if (!(key in listTypes)) {
      warnings.push(`${key} not found in listTypes: ${JSON.stringify(listTypes)}`);
      listTypes[key] = BULLET;
    }
    return listTypes[key];
  }

  // 1. Use DOMParser to parse the document fragment, as jQuery runs into trouble with xmlns in the html tag.
  const results = [];
  const parser = new DOMParser();
  const $doc = $(parser.parseFromString(html, 'text/html'));

  // 2. Parse the style tag in head names to extract list name and the associated listType (bullet vs other)
  //    `bullet` is used at all indentations, while numbers switch between `alpha-lower`, `roman-lower`, etc.
  for (const el of Array.from($doc.find('style'))) {
    const listStylePattern = /@list\s+(l\d+):\s*level(\d+)\s+{([^}]*)}/g; // e.g.: @list l0:level1 {...}
    groups = listStylePattern.exec(el.innerHTML);
    while (groups != null) {
      listName = groups[groups.length - 3];
      listLevel = groups[groups.length - 2];
      listStyle = groups[groups.length - 1];
      listType = _hasBulletStyle(listStyle) ? BULLET : NUMBER;
      setListStyle(listName, listLevel, listType);
      groups = listStylePattern.exec(el.innerHTML)!;
    }
  }

  // 3. Iterate over all nodes in the body.
  //     - A list item is a P or a header with an mso-list style that corresponds to a list processed above.
  //     - A list item will contain a span with `mso-list: Ignore` that contains an ascii representation of
  //       the bullet + indentation, which we remove.
  //     - After this pre-processing, we can process the node in the usual way.

  // Attach the body to a fragment created from document, so that body.ownerDocument.defaultView
  // will resolve to window properly, allowing us to use $.css() in wyciwyp's processNode.
  // See: https://github.com/jquery/jquery/issues/2086
  const $body = $doc.find('body');
  document.createDocumentFragment().appendChild($body[0]);

  // eslint-disable-next-line no-restricted-syntax
  const extendedOptions = _.extend(options, {
    extraSanitizeText(contents: string, textNode: Node): string {
      // textNode is a TEXT node, whose parent may have MS-styles
      const style = $(textNode.parentNode!).attr('style');
      contents = resolveSymbolsToUnicode(contents, style, warnings);
      return contents;
    },
  });
  for (const el of Array.from($body.children())) {
    switch (el.nodeName) {
      case 'P':
      case 'H1':
      case 'H2':
      case 'H3':
      case 'H4':
      case 'H5':
      case 'H6':
        groups = /mso-list:\s*(l\d+)\s+level(\d+)/.exec(
          // @ts-expect-error
          el.attributes.style != null ? el.attributes.style.value : undefined
        );
        if (groups != null) {
          listName = groups[groups.length - 2];
          listLevel = groups[groups.length - 1];
          listType = getListStyle(listName, listLevel);
          _removeWhere(el, _isListBullet);
          const processed = processNode(el, extendedOptions);
          for (let i = 0; i < processed.length; i++) {
            const result = processed[i];
            result.indentation = Number(listLevel);
            result.listType = i === 0 ? listType : null;
          }
          results.push(...Array.from(processed || []));
          continue;
        }
        break;
      default:
        break;
    }
    results.push(...Array.from(processNode(el, extendedOptions) || []));
  }
  return results;
}

function parseStyleAttribute(style: string, attribute: string): string | null {
  if (style == null) {
    return null;
  }
  const match = new RegExp(`\\b${attribute}\\s*:\\s*([^;]+)`).exec(style);
  if (match == null) {
    return null;
  }
  return match[1];
}

export {isWord, processWord};
